import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  createView,
  deleteView,
  listViews,
  setDefault,
  unsetDefault,
  updateView,
} from "@/api/views";
import type {
  CompanyDetailLayoutConfig,
  SavedViewCreate,
  SavedViewRead,
  SavedViewUpdate,
} from "@/api/types";

const ENTITY = "company_detail_layout";

function activeStorageKey(slug: string): string {
  return `prism.${slug}.detail.activeViewId`;
}

function readActiveId(slug: string): number | null {
  try {
    const raw = window.localStorage.getItem(activeStorageKey(slug));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function persistActiveId(slug: string, id: number | null) {
  try {
    if (id == null) window.localStorage.removeItem(activeStorageKey(slug));
    else window.localStorage.setItem(activeStorageKey(slug), String(id));
  } catch {
    // QuotaExceeded / private mode — silent. Active view falls
    // back to the default on next mount.
  }
}

/** Read the layout config off a SavedView. Prefer the structured
 *  `layout_config` field; fall back to `filters` for backends
 *  that haven't shipped the dedicated slot yet — both store the
 *  same shape. */
export function readLayoutConfig(
  view: SavedViewRead,
): CompanyDetailLayoutConfig {
  if (view.layout_config) return view.layout_config;
  return view.filters as CompanyDetailLayoutConfig;
}

/**
 * Manages the list of "company_detail_layout" SavedViews for a
 * project, plus the operator's currently-active view.
 *
 * - listing happens once on slug change; CRUD actions optimistically
 *   merge into the local cache and fall back to a refetch on
 *   error so the dropdown stays consistent
 * - active view id is persisted per-project in localStorage so
 *   the same view is re-applied on reload; null means "no view
 *   selected, use the project default"
 */
export function useLayoutViews(slug: string) {
  const [views, setViews] = useState<SavedViewRead[]>([]);
  const [loading, setLoading] = useState<boolean>(!!slug);
  const [activeId, setActiveIdState] = useState<number | null>(() =>
    slug ? readActiveId(slug) : null,
  );

  // Initial fetch + re-fetch on slug change.
  const refresh = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await listViews(slug, ENTITY);
      setViews(data);
    } catch {
      // Silent — dropdown will be empty until next refresh.
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      setViews([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    void refresh();
    setActiveIdState(readActiveId(slug));
  }, [slug, refresh]);

  const setActiveId = useCallback(
    (id: number | null) => {
      setActiveIdState(id);
      persistActiveId(slug, id);
    },
    [slug],
  );

  const defaultView = useMemo(
    () => views.find((v) => v.is_default) ?? null,
    [views],
  );

  const activeView = useMemo(
    () => (activeId != null ? views.find((v) => v.id === activeId) ?? null : null),
    [activeId, views],
  );

  const create = useCallback(
    async (payload: SavedViewCreate): Promise<SavedViewRead | null> => {
      try {
        const created = await createView(slug, payload);
        setViews((prev) => [...prev, created]);
        toast.success(`View "${created.name}" saved`);
        return created;
      } catch (err) {
        toast.error(extractError(err, "Couldn't save view"));
        return null;
      }
    },
    [slug],
  );

  const update = useCallback(
    async (
      id: number,
      payload: SavedViewUpdate,
    ): Promise<SavedViewRead | null> => {
      try {
        const updated = await updateView(slug, id, payload);
        setViews((prev) => prev.map((v) => (v.id === id ? updated : v)));
        return updated;
      } catch (err) {
        toast.error(extractError(err, "Couldn't update view"));
        return null;
      }
    },
    [slug],
  );

  const remove = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        await deleteView(slug, id);
        setViews((prev) => prev.filter((v) => v.id !== id));
        if (activeId === id) setActiveId(null);
        return true;
      } catch (err) {
        toast.error(extractError(err, "Couldn't delete view"));
        return false;
      }
    },
    [slug, activeId, setActiveId],
  );

  const markDefault = useCallback(
    async (id: number, on: boolean): Promise<void> => {
      try {
        const updated = on
          ? await setDefault(slug, id)
          : await unsetDefault(slug, id);
        // setDefault flips is_default off on every other view —
        // refetch to pick up the side effect cleanly.
        setViews((prev) =>
          prev.map((v) => {
            if (v.id === updated.id) return updated;
            if (on && v.is_default) return { ...v, is_default: false };
            return v;
          }),
        );
      } catch (err) {
        toast.error(extractError(err, "Couldn't update default view"));
      }
    },
    [slug],
  );

  return {
    views,
    loading,
    activeId,
    activeView,
    defaultView,
    setActiveId,
    create,
    update,
    remove,
    markDefault,
    refresh,
  };
}

function extractError(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string" && m.length > 0) return m;
  }
  return fallback;
}
